import { useMemo, useState } from 'react';
import { parseYoutubeId, type ResourceDoc, type ResourceKind } from '../../lib/data-model';
import { useSaver } from './ListAdmin';
import { UploadField } from './UploadField';

export type ResourceFormProps = {
  sectionId: string;
  courseId: string;
  order: number;
  existing?: ResourceDoc & { id: string };
  onDone: () => void;
  onCancel: () => void;
};

const KINDS: Array<{ value: ResourceKind; label: string; icon: string }> = [
  { value: 'video', label: 'Video', icon: '▶' },
  { value: 'pdf', label: 'PDF', icon: '▤' },
  { value: 'file', label: 'File', icon: '⧉' },
  { value: 'link', label: 'Link', icon: '↗' }
];

/** Inline (no modal) new/edit resource form, used inside the chapter editor. */
export function ResourceForm({ sectionId, courseId, order, existing, onDone, onCancel }: ResourceFormProps) {
  const [kind, setKind] = useState<ResourceKind>(existing?.kind ?? 'link');
  const [title, setTitle] = useState(existing?.title ?? '');
  const [meta, setMeta] = useState(existing?.meta ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [yt, setYt] = useState(existing?.youtubeId ? `https://www.youtube.com/watch?v=${existing.youtubeId}` : '');
  const [storagePath, setStoragePath] = useState(existing?.storagePath ?? '');
  const [fileName, setFileName] = useState(existing?.fileName ?? '');
  const hidden = existing?.hidden ?? false;

  const id = useMemo(() => existing?.id ?? `${sectionId}-r${Date.now().toString(36)}`, [existing, sectionId]);

  const build = (): Record<string, unknown> => {
    const data: Record<string, unknown> = { sectionId, courseId, order, kind, title: title.trim() || (existing?.title ?? ''), hidden };
    if (meta.trim()) data.meta = meta.trim();
    if (kind === 'link') {
      if (url.trim()) data.url = url.trim();
    } else if (kind === 'video') {
      const vid = parseYoutubeId(yt);
      if (vid) data.youtubeId = vid;
    } else if (storagePath) {
      data.storagePath = storagePath;
      if (fileName) data.fileName = fileName;
    } else if (url.trim()) {
      data.url = url.trim();
    }
    return data;
  };

  const { errors, saving, save } = useSaver('resources', id, build, onDone);

  const parsed = kind === 'video' ? parseYoutubeId(yt) : null;
  const fileMode = kind === 'pdf' || kind === 'file';

  const setUpload = (v: { path?: string; url?: string; name?: string }) => {
    setStoragePath(v.path ?? '');
    setFileName(v.path ? v.name ?? '' : '');
    if (v.path) setUrl('');
  };

  return (
    <div className="res-form">
      <div className="kind-chips">
        {KINDS.map(k => (
          <button
            key={k.value}
            type="button"
            className="kind-chip"
            aria-pressed={kind === k.value}
            onClick={() => {
              setKind(k.value);
              setUrl('');
              setStoragePath('');
              setFileName('');
            }}
          >
            <span aria-hidden>{k.icon}</span> {k.label}
          </button>
        ))}
      </div>

      <div className="row2">
        <div className="field">
          <label>Title</label>
          <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Lecture 4 — Integration by parts" />
          {errors.title ? <div className="error">{errors.title}</div> : null}
        </div>
        <div className="field">
          <label>Meta line</label>
          <input type="text" value={meta} onChange={e => setMeta(e.target.value)} placeholder="Lecture 4 · 38 min" />
          {errors.meta ? <div className="error">{errors.meta}</div> : null}
        </div>
      </div>

      {kind === 'link' && (
        <div className="field">
          <label>URL (https)</label>
          <input type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" />
          {errors.url ? <div className="error">{errors.url}</div> : null}
        </div>
      )}

      {kind === 'video' && (
        <div className="field">
          <label>YouTube link or id</label>
          <input type="text" value={yt} onChange={e => setYt(e.target.value)} placeholder="https://youtu.be/… or an 11-character id" />
          {errors.youtubeId ? <div className="error">{errors.youtubeId}</div> : null}
          {parsed && (
            <div style={{ marginTop: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
              <img
                src={`https://img.youtube.com/vi/${parsed}/hqdefault.jpg`}
                alt=""
                style={{ width: 110, borderRadius: 10, border: '1px solid var(--line)' }}
              />
              <div>
                <div className="mono" style={{ fontSize: 12, color: 'var(--ink3)' }}>{parsed}</div>
                <div className="hint">Thumbnail verified against YouTube</div>
              </div>
            </div>
          )}
        </div>
      )}

      {fileMode && (
        <div className="field">
          <label>File</label>
          <UploadField courseId={courseId} value={{ path: storagePath || undefined, name: fileName || undefined }} onChange={setUpload} />
          {errors.storagePath ? <div className="error">{errors.storagePath}</div> : null}
          <div style={{ marginTop: 10 }}>
            <input
              type="url"
              value={url}
              onChange={e => {
                setUrl(e.target.value);
                if (e.target.value.trim()) {
                  setStoragePath('');
                  setFileName('');
                }
              }}
              placeholder="…or paste a direct https link (upload wins if present)"
            />
            {errors.url ? <div className="error">{errors.url}</div> : null}
          </div>
        </div>
      )}

      {errors._ ? <div className="error">{errors._}</div> : null}

      <div className="res-form-foot">
        <button className="btn btn-outline btn-sm" onClick={onCancel}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : existing ? 'Update resource' : 'Add resource'}
        </button>
      </div>
    </div>
  );
}
